import { Svg } from '@innovyze/stylovyze/lib/components/Svg';
import { SeverityLevel } from '@innovyze/stylovyze/lib/types';
import { CircularProgress, IconButton } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';
import { Link } from 'react-router-dom';
import { getNotificationIcon, isArchived, isDeleted } from '../utils';
import DeleteIcon from './Delete.svg';
import { ErrorIndicator, IconCellContainer, InfoIndicator, SuccessIndicator, WarningIndicator, } from './NotificationManagement.styles';
export const getParsedMetaData = (metaData) => {
    let metaDataObj = metaData;
    while (typeof metaDataObj === 'string') {
        metaDataObj = JSON.parse(metaDataObj);
    }
    const link = metaDataObj?.LINK ?? '';
    const linkText = metaDataObj?.LINK_TEXT ?? '';
    return { link, linkText };
};
const getFilteredItems = (notificationItems, searchText, typeFilter, startDate, endDate, timeZoneIANA, isDeleteNotificationEnabled) => {
    /* sample input:
        startDate: 2023-11-09T00:00:00.000-08:00
        timeZoneIANA: America/New_York
        output: 1699506000000
    */
    const startTimeStamp = startDate
        ? DateTime.fromISO(startDate.slice(0, startDate.indexOf('T')), {
            zone: timeZoneIANA,
        }).toMillis()
        : undefined;
    const endTimeStamp = endDate
        ? DateTime.fromISO(endDate.slice(0, endDate.indexOf('T')), {
            zone: timeZoneIANA,
        })
            .plus({ days: 1 })
            .toMillis() - 1
        : undefined;
    return notificationItems.filter(({ status, notificationType, notificationText, timestamp }) => {
        let match = true;
        if ((isDeleteNotificationEnabled
            ? isDeleted(status)
            : isArchived(status)) ||
            (typeFilter !== 'all' && notificationType !== typeFilter) ||
            (searchText &&
                notificationText
                    .toUpperCase()
                    .indexOf(searchText.toUpperCase()) === -1) ||
            (startTimeStamp && timestamp < startTimeStamp) ||
            (endTimeStamp && timestamp > endTimeStamp)) {
            match = false;
        }
        return match;
    });
};
export const getTotalNumberOfPages = (notificationItems, rowsPerPage, searchText, typeFilter, startDate, endDate, timeZoneIANA, isDeleteNotificationEnabled) => {
    return Math.ceil(getFilteredItems(notificationItems, searchText, typeFilter, startDate, endDate, timeZoneIANA, isDeleteNotificationEnabled).length / rowsPerPage);
};
const getLinkCell = (id, metaData) => {
    const { link, linkText } = getParsedMetaData(metaData);
    return link && linkText ? (React.createElement(Link, { to: link, key: `${id}-link` }, linkText)) : null;
};
const getIndicator = (severityLevel) => {
    switch (severityLevel) {
        case SeverityLevel.ERROR:
            return React.createElement(ErrorIndicator, null);
        case SeverityLevel.WARNING:
            return React.createElement(WarningIndicator, null);
        case SeverityLevel.SUCCESS:
            return React.createElement(SuccessIndicator, null);
        default:
            return React.createElement(InfoIndicator, null);
    }
};
export const getNotificationItemsInPage = (page, rowsPerPage, timeStampOrder, notificationItems, searchText, typeFilter, startDate, endDate, timeZoneIANA, isDeleteNotificationEnabled) => {
    return [
        ...getFilteredItems(notificationItems, searchText, typeFilter, startDate, endDate, timeZoneIANA, isDeleteNotificationEnabled),
    ]
        .sort((a, b) => timeStampOrder === 'asc'
        ? a.timestamp - b.timestamp
        : b.timestamp - a.timestamp)
        .slice((page - 1) * rowsPerPage, page * rowsPerPage);
};
export const getRowsInPage = (page, rowsPerPage, timeStampOrder, notificationItems, deleteIds, formatDate, deleteNotificationsByIds, searchText, typeFilter, startDate, endDate, timeZoneIANA, isDeleteNotificationEnabled) => getNotificationItemsInPage(page, rowsPerPage, timeStampOrder, notificationItems, searchText, typeFilter, startDate, endDate, timeZoneIANA, isDeleteNotificationEnabled).map(({ id, timestamp, notificationType, notificationText, severityLevel, metaData, }) => {
    const deleteIconDisabled = deleteIds.some(deleteId => deleteId === id);
    return {
        key: id,
        cells: [
            React.createElement(IconCellContainer, { key: `${id}-icon` },
                getIndicator(severityLevel),
                getNotificationIcon(severityLevel)),
            notificationType,
            {
                content: notificationText,
                ellipsis: true,
            },
            getLinkCell(id, metaData),
            formatDate({
                date: timestamp,
                format: 'date-time',
                dateTimeSeparator: ' ',
            }),
            React.createElement(IconButton, { "aria-label": "delete", key: `${id}-delete`, onClick: () => deleteNotificationsByIds([id]), disabled: deleteIconDisabled },
                React.createElement(React.Fragment, null,
                    React.createElement(Svg, { svg: DeleteIcon }),
                    deleteIconDisabled && (React.createElement(CircularProgress, { size: 40, sx: { position: 'absolute' } })))),
        ],
    };
});
// --- For testing purpose only
export const createSampleItems = (notificationItems) => {
    if (notificationItems.length === 0)
        return [];
    const item = notificationItems[0];
    const sampleItems = [];
    const getSeverityLevel = (i) => {
        switch (i % 4) {
            case 0:
                return SeverityLevel.ERROR;
            case 1:
                return SeverityLevel.SUCCESS;
            case 2:
                return SeverityLevel.WARNING;
            default:
                return SeverityLevel.INFO;
        }
    };
    for (let i = 0; i < 643; i++) {
        sampleItems.push({
            ...item,
            id: `${item.id}--${i}`,
            severityLevel: getSeverityLevel(i),
            notificationText: `${i}__${item.notificationText}`,
            timestamp: item.timestamp + 1000000 * (643 - i),
        });
    }
    return sampleItems;
};
